import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
    AppBar,
    Toolbar,
    Box,
    IconButton,
    MenuItem,
    MenuList,
    Popper,
    Paper,
    ClickAwayListener,
    Grow,
    Divider,
    ListItemIcon,
    ListItemText,
    useMediaQuery,
    useTheme,
    Menu, Typography, Chip, Badge, CircularProgress, Fade
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import SyncIcon from '@mui/icons-material/Sync';
import UpdateIcon from '@mui/icons-material/Update';

import { TENANT_LOGO_HEADER_URL, TENANT_THEME_COLOR } from '../../config/tenant';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { useSyncTime } from '../../hooks/useSyncTime';
import versionService from '../../services/versionService';
import { syncAllData } from '../../services/syncService';

import {MAIN_MENU_ITEMS} from "../../config/menu";
import featureVisibilityService from "../../services/featureVisibilityService";

const Header = ({ handleLogout }) => {
    const navigate = useNavigate();
    const isOnline = useOnlineStatus();
    const lastSyncTime = useSyncTime();
    const [appVersion, setAppVersion] = useState('...');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
    const isTablet = useMediaQuery(theme.breakpoints.down('md')); // < 900px
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg')); // >= 1200px
    const location = useLocation();
    
    // Определяем ориентацию для планшетов
    const isPortrait = useMediaQuery('(orientation: portrait)');
    const isTabletPortrait = isTablet && !isMobile && isPortrait;
    
    // Состояния для синхронизации и обновления
    const [isSyncing, setIsSyncing] = useState(false);
    const [isCheckingUpdate, setIsCheckingUpdate] = useState(false);
    
    // Загрузка версии при монтировании
    useEffect(() => {
        // Получаем текущую версию из versionService
        const currentVersion = versionService.getCurrentVersion();
        if (currentVersion) {
            setAppVersion(currentVersion);
        } else {
            // Если версия еще не загружена, пробуем загрузить
            fetch('/version.json?t=' + Date.now())
                .then(res => res.json())
                .then(data => {
                    if (data.version) {
                        setAppVersion(data.version);
                    }
                })
                .catch(() => {
                    setAppVersion('0.2.0'); // Fallback
                });
        }
    }, []);
    
    // Обработчик синхронизации с индикацией
    const handleSync = useCallback(async () => {
        setIsSyncing(true);
        try {
            await syncAllData();
        } finally {
            setTimeout(() => setIsSyncing(false), 1000);
        }
    }, []);
    

    const user = useMemo(() => ({
        firstName: localStorage.getItem('firstName') || 'Unknown',
        lastName: localStorage.getItem('lastName') || 'User',
        role: localStorage.getItem('role') || 'Unknown',
    }), []);

    // Check if user has ROLE_ADMIN
    const isAdmin = useMemo(() => {
        try {
            const roles = JSON.parse(localStorage.getItem('roles') || '[]');
            return roles.includes('ROLE_ADMIN');
        } catch (e) {
            return false;
        }
    }, []);

    // Feature visibility — reactive: re-read on focus/sync
    const [visibilityKeys, setVisibilityKeys] = useState(() => featureVisibilityService.getVisibleFeatures());

    useEffect(() => {
        const reload = () => setVisibilityKeys(featureVisibilityService.getVisibleFeatures());
        window.addEventListener('focus', reload);
        window.addEventListener('storage', (e) => {
            if (e.key === 'featureVisibility') reload();
        });
        const interval = setInterval(reload, 10000);
        return () => {
            window.removeEventListener('focus', reload);
            window.removeEventListener('storage', reload);
            clearInterval(interval);
        };
    }, []);

    // Filter menu items by feature visibility
    const visibleMenuItems = useMemo(() => {
        const isVis = (key) => featureVisibilityService.isVisible(key, visibilityKeys);

        return MAIN_MENU_ITEMS
            .filter(item => {
                if (item.featureKey && !isVis(item.featureKey)) return false;
                return true;
            })
            .map(item => {
                if (!item.subMenu) return item;
                const filteredSub = item.subMenu.filter(sub =>
                    !sub.featureKey || isVis(sub.featureKey)
                );
                if (filteredSub.length === 0) return null;
                return { ...item, subMenu: filteredSub };
            })
            .filter(Boolean);
    }, [visibilityKeys]);

    // Состояния для главного меню и подпункта
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [subMenuAnchor, setSubMenuAnchor] = useState(null);
    const [activeSubMenuId, setActiveSubMenuId] = useState(null);

    // Меню пользователя (иконка профиля)
    const [userMenuAnchor, setUserMenuAnchor] = useState(null);

    // ----------------------
    // Обработчики
    // ----------------------

    // Открыть/закрыть главное меню
    const handleMenuOpen = useCallback((e) => {
        setMenuAnchor(e.currentTarget);
    }, []);

    const handleMenuClose = useCallback(() => {
        setMenuAnchor(null);
        setSubMenuAnchor(null);
        setActiveSubMenuId(null);
    }, []);

    const handleClickAway = useCallback((event) => {
        // Игнорируем клики на подменю
        const target = event.target;
        if (target && target.closest) {
            const isSubmenuClick = target.closest('[data-submenu="true"]');
            if (isSubmenuClick) {
                return;
            }
        }
        handleMenuClose();
    }, [handleMenuClose]);

    // Открыть подменю
    const handleSubMenuOpen = useCallback((e, itemIndex) => {
        setActiveSubMenuId(itemIndex);
        setSubMenuAnchor(e.currentTarget);
    }, []);

    // Меню пользователя
    const handleUserMenuOpen = useCallback((event) => {
        setUserMenuAnchor(event.currentTarget);
    }, []);

    const handleUserMenuClose = useCallback(() => {
        setUserMenuAnchor(null);
    }, []);

    return (
        <AppBar position="fixed" sx={{ 
            backgroundColor: TENANT_THEME_COLOR,
            zIndex: theme.zIndex.drawer + 1,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
            <Toolbar 
                sx={{ 
                    justifyContent: 'space-between',
                    minHeight: { xs: 56, sm: 64 },
                    px: { xs: 1, sm: 2 },
                    gap: 1
                }}
            >
                {/* Левая часть: лого и меню */}
                <Box display="flex" alignItems="center" sx={{ flexShrink: 0 }}>
                    <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
                        <img
                            src={TENANT_LOGO_HEADER_URL}
                            alt="Logo"
                            style={{ 
                                maxHeight: isMobile ? '32px' : '40px', 
                                marginRight: isMobile ? '8px' : '12px' 
                            }}
                        />
                    </Link>

                    <IconButton 
                        color="inherit" 
                        onClick={handleMenuOpen}
                        sx={{ 
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                        }}
                        size={isMobile ? "small" : "medium"}
                    >
                        <MenuIcon />
                    </IconButton>

                    {/* Главное меню */}
                    <Popper
                        open={Boolean(menuAnchor)}
                        anchorEl={menuAnchor}
                        role={undefined}
                        transition
                        disablePortal
                        style={{ zIndex: 1300 }}
                        placement="bottom-start"
                        modifiers={[
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, 8],
                                },
                            },
                        ]}
                    >
                        {({ TransitionProps, placement }) => (
                            <Grow
                                {...TransitionProps}
                                style={{
                                    transformOrigin: 'top left',
                                }}
                                timeout={200}
                            >
                                <Paper
                                    sx={{
                                        minWidth: 280,
                                        maxWidth: 320,
                                        borderRadius: 2,
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                        border: '1px solid rgba(0,0,0,0.08)',
                                        overflow: 'hidden'
                                    }}
                                >
                                    <ClickAwayListener onClickAway={handleClickAway}>
                                        <Box>
                                            {/* Заголовок меню */}
                                            <Box sx={{ 
                                                px: 2, 
                                                py: 1.5, 
                                                backgroundColor: '#f5f5f5',
                                                borderBottom: '1px solid rgba(0,0,0,0.08)'
                                            }}>
                                                <Typography variant="caption" sx={{ 
                                                    fontWeight: 600, 
                                                    color: 'text.secondary',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: 0.5
                                                }}>
                                                    Navigation
                                                </Typography>
                                            </Box>
                                            
                                            <MenuList sx={{ py: 1 }}>
                                                {visibleMenuItems.map((item, idx, arr) => {
                                                    const hasSubMenu = item.subMenu?.length > 0;
                                                    const isActive = item.link && location.pathname === item.link;

                                                    return (
                                                        <MenuItem
                                                            key={`menu-item-${item.label}`}
                                                            onClick={
                                                                hasSubMenu
                                                                    ? (e) => {
                                                                        e.preventDefault();
                                                                        handleSubMenuOpen(e, idx);
                                                                    }
                                                                    : () => {
                                                                        handleMenuClose();
                                                                        if (item.link) {
                                                                            navigate(item.link);
                                                                        }
                                                                    }
                                                            }
                                                            onMouseEnter={
                                                                (e) => {
                                                                    if (hasSubMenu) {
                                                                        handleSubMenuOpen(e, idx);
                                                                    } else {
                                                                        setActiveSubMenuId(null);
                                                                        setSubMenuAnchor(null);
                                                                    }
                                                                }
                                                            }
                                                            sx={{
                                                                py: 1.5,
                                                                px: 2,
                                                                mx: 1,
                                                                my: 0.25,
                                                                borderRadius: 1,
                                                                transition: 'all 0.2s',
                                                                cursor: hasSubMenu ? 'default' : 'pointer',
                                                                backgroundColor: isActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                                                '&:hover': {
                                                                    backgroundColor: isActive ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                                                                },
                                                                '& .MuiListItemIcon-root': {
                                                                    minWidth: 36,
                                                                    color: isActive ? 'primary.main' : 'text.secondary'
                                                                },
                                                                '& .MuiListItemText-primary': {
                                                                    fontWeight: isActive ? 600 : 400,
                                                                    color: isActive ? 'primary.main' : 'text.primary'
                                                                }
                                                            }}
                                                        >
                                                            <Box display="flex" alignItems="center" width="100%">
                                                                {item.icon && (
                                                                    <ListItemIcon>
                                                                        {React.cloneElement(item.icon, {
                                                                            sx: { fontSize: 20 }
                                                                        })}
                                                                    </ListItemIcon>
                                                                )}
                                                                <ListItemText
                                                                    primary={item.label}
                                                                    primaryTypographyProps={{
                                                                        fontSize: '0.875rem'
                                                                    }}
                                                                />
                                                                {hasSubMenu && (
                                                                    <ArrowRightIcon
                                                                        sx={{
                                                                            fontSize: 18,
                                                                            color: 'text.secondary',
                                                                            ml: 'auto'
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        </MenuItem>
                                                    );
                                                })}
                                            </MenuList>
                                        </Box>
                                    </ClickAwayListener>
                                </Paper>
                            </Grow>
                        )}
                    </Popper>

                    {/* Подменю (второй Popper) */}
                    <Popper
                        open={Boolean(subMenuAnchor) && activeSubMenuId !== null}
                        anchorEl={subMenuAnchor}
                        placement="right-start"
                        disablePortal
                        style={{ zIndex: 1350 }}
                        modifiers={[
                            {
                                name: 'offset',
                                options: {
                                    offset: [8, -8],
                                },
                            },
                        ]}
                    >
                        <Grow
                            in={Boolean(subMenuAnchor) && activeSubMenuId !== null}
                            style={{ transformOrigin: 'left center' }}
                            timeout={150}
                        >
                            <Paper
                                data-submenu="true"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                    minWidth: 220,
                                    borderRadius: 2,
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                    border: '1px solid rgba(0,0,0,0.08)',
                                    overflow: 'hidden',
                                    position: 'relative',
                                    zIndex: 1350,
                                    pointerEvents: 'auto'
                                }}
                            >
                                <Box>
                                    {activeSubMenuId != null && visibleMenuItems[activeSubMenuId]?.subMenu && (
                                        <>
                                            <Box sx={{ 
                                                px: 2, 
                                                py: 1, 
                                                backgroundColor: '#f5f5f5',
                                                borderBottom: '1px solid rgba(0,0,0,0.08)'
                                            }}>
                                                <Typography variant="caption" sx={{ 
                                                    fontWeight: 600, 
                                                    color: 'text.secondary',
                                                    fontSize: '0.75rem'
                                                }}>
                                                    {visibleMenuItems[activeSubMenuId].label}
                                                </Typography>
                                            </Box>
                                            <MenuList sx={{ py: 1 }}>
                                                {visibleMenuItems[activeSubMenuId].subMenu
                                                    .filter(subItem => !subItem.adminOnly || isAdmin)
                                                    .map((subItem) => {
                                                    const isActive = location.pathname === subItem.link;

                                                    return (
                                                        <MenuItem
                                                            key={`sub-menu-item-${subItem.label}`}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                handleMenuClose();
                                                                setTimeout(() => {
                                                                    navigate(subItem.link);
                                                                }, 50);
                                                            }}
                                                            sx={{
                                                                py: 1.25,
                                                                px: 2,
                                                                mx: 1,
                                                                my: 0.25,
                                                                borderRadius: 1,
                                                                fontSize: '0.875rem',
                                                                transition: 'all 0.2s',
                                                                backgroundColor: isActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                                                color: isActive ? 'primary.main' : 'text.primary',
                                                                fontWeight: isActive ? 600 : 400,
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    backgroundColor: isActive ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                                                                }
                                                            }}
                                                        >
                                                            {subItem.label}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </MenuList>
                                        </>
                                    )}
                                </Box>
                            </Paper>
                        </Grow>
                    </Popper>
                </Box>

                {/* Правая часть: статусы и профиль */}
                <Box 
                    display="flex" 
                    alignItems="center" 
                    gap={1}
                    sx={{ 
                        flexWrap: 'nowrap',
                        overflow: 'visible',
                        flex: 1,
                        justifyContent: 'flex-end'
                    }}
                >
                    {/* Современный блок статусов */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: { xs: 1, sm: 1.5 },
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '24px',
                        px: { xs: 1.5, sm: 2 },
                        py: 0.75
                    }}>
                        
                        {/* Сеть */}
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            gap: 0.5
                        }}>
                            {isOnline ? (
                                <WifiIcon sx={{ fontSize: 20, color: '#4caf50' }} />
                            ) : (
                                <WifiOffIcon sx={{ fontSize: 20, color: '#f44336' }} />
                            )}
                            <Typography sx={{ 
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                fontWeight: 600,
                                color: 'white',
                                display: { xs: 'none', sm: 'block' }
                            }}>
                                {isOnline ? 'Online' : 'Offline'}
                            </Typography>
                        </Box>
                        
                        <Divider orientation="vertical" flexItem sx={{ 
                            backgroundColor: 'rgba(255,255,255,0.3)', 
                            height: 20,
                            alignSelf: 'center'
                        }} />
                        
                        {/* Синхронизация */}
                        <Box 
                            onClick={handleSync}
                            sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 0.5,
                                cursor: 'pointer',
                                position: 'relative',
                                transition: 'all 0.2s',
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            {isSyncing ? (
                                <CircularProgress size={20} sx={{ color: 'white' }} />
                            ) : (
                                <SyncIcon sx={{ 
                                    fontSize: 20, 
                                    color: 'white',
                                    animation: isSyncing ? 'spin 1s linear infinite' : 'none',
                                    '@keyframes spin': {
                                        '0%': { transform: 'rotate(0deg)' },
                                        '100%': { transform: 'rotate(360deg)' }
                                    }
                                }} />
                            )}
                            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Typography sx={{ 
                                    fontSize: '0.7rem', 
                                    color: 'rgba(255,255,255,0.7)',
                                    lineHeight: 1
                                }}>
                                    {isSyncing ? 'Syncing...' : 'Last sync'}
                                </Typography>
                                <Typography sx={{ 
                                    fontSize: '0.875rem', 
                                    fontWeight: 700,
                                    color: 'white',
                                    lineHeight: 1
                                }}>
                                    {isSyncing ? 'Now' : lastSyncTime}
                                </Typography>
                            </Box>
                        </Box>
                        
                        <Divider orientation="vertical" flexItem sx={{ 
                            backgroundColor: 'rgba(255,255,255,0.3)', 
                            height: 20,
                            alignSelf: 'center'
                        }} />
                        
                        {/* Версия приложения */}
                        <Box 
                            onClick={async () => {
                                setIsCheckingUpdate(true);
                                try {
                                    const hasUpdate = await versionService.checkForUpdate();
                                    if (!hasUpdate) {
                                        // Show notification that app is up to date
                                        const notification = document.createElement('div');
                                        notification.style.cssText = `
                                            position: fixed;
                                            top: 80px;
                                            right: 20px;
                                            background: #2196F3;
                                            color: white;
                                            padding: 12px 20px;
                                            border-radius: 8px;
                                            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                                            z-index: 10000;
                                            font-family: Arial, sans-serif;
                                            font-size: 14px;
                                            animation: slideIn 0.3s ease-out;
                                        `;
                                        notification.textContent = 'App is up to date!';
                                        document.body.appendChild(notification);
                                        setTimeout(() => notification.remove(), 3000);
                                    }
                                } finally {
                                    setIsCheckingUpdate(false);
                                }
                            }}
                            sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 0.5,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            {isCheckingUpdate ? (
                                <CircularProgress size={20} sx={{ color: 'white' }} />
                            ) : (
                                <UpdateIcon sx={{ fontSize: 20, color: 'white' }} />
                            )}
                            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Typography sx={{ 
                                    fontSize: '0.7rem', 
                                    color: 'rgba(255,255,255,0.7)',
                                    lineHeight: 1
                                }}>
                                    Version
                                </Typography>
                                <Typography sx={{ 
                                    fontSize: '0.875rem', 
                                    fontWeight: 700,
                                    color: 'white',
                                    lineHeight: 1,
                                    letterSpacing: 0.5
                                }}>
                                    v{appVersion}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Меню профиля */}
                    <IconButton 
                        color="inherit" 
                        onClick={handleUserMenuOpen}
                        size={isMobile ? "small" : "medium"}
                        sx={{ 
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                            flexShrink: 0,
                            ml: 0.5
                        }}
                    >
                        <AccountCircleIcon />
                    </IconButton>
                    
                    <Menu
                        anchorEl={userMenuAnchor}
                        open={Boolean(userMenuAnchor)}
                        onClose={handleUserMenuClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        PaperProps={{
                            sx: {
                                mt: 1.5,
                                minWidth: 240,
                                borderRadius: 2,
                                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                border: '1px solid rgba(0,0,0,0.08)',
                                overflow: 'hidden'
                            }
                        }}
                        MenuListProps={{
                            sx: { py: 0 }
                        }}
                    >
                        <Box sx={{ 
                            px: 2.5, 
                            py: 2, 
                            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                            color: 'white'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ 
                                    width: 48, 
                                    height: 48, 
                                    borderRadius: '50%', 
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <PersonIcon sx={{ fontSize: 28, color: 'white' }} />
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                                        {user.firstName} {user.lastName}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        
                        <Box sx={{ py: 1 }}>

                            

                            
                            <MenuItem 
                                onClick={handleLogout} 
                                sx={{ 
                                    py: 1.5,
                                    px: 2.5,
                                    mx: 1,
                                    borderRadius: 1,
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        backgroundColor: 'rgba(244, 67, 54, 0.08)',
                                        '& .MuiListItemIcon-root': {
                                            color: '#f44336'
                                        },
                                        '& .MuiListItemText-primary': {
                                            color: '#f44336'
                                        }
                                    }
                                }}
                            >
                                <ListItemIcon>
                                    <LogoutIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                </ListItemIcon>
                                <ListItemText 
                                    primary="Logout" 
                                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                                />
                            </MenuItem>
                        </Box>
                    </Menu>
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default Header;
